import { useCallback, useEffect, useMemo, useState } from "react";
import { Key, Trash, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { WorkerCredentialsDialog } from "../components/CredentialDialogs";
import api, { formatApiError } from "../lib/api";

const blankWorkerForm = () => ({
  name: "",
  user_id: "",
  email: "",
  password: "",
  role: "officer",
  reports_to_id: "",
});

export default function UserManagementPageV4({ user }) {
  const [team, setTeam] = useState([]);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankWorkerForm);

  const loadTeam = useCallback(async () => {
    try {
      const response = await api.get("/team");
      setTeam(response.data);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  }, []);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const parentNames = useMemo(() => {
    return new Map(team.map((member) => [member.id, member.name]));
  }, [team]);

  const reportingOptions = useMemo(() => {
    return team
      .filter((member) => member.id !== user.id)
      .map((member) => ({
        id: member.id,
        label: `${member.name} · ${member.user_id || member.role}`,
      }));
  }, [team, user.id]);

  const canManageUsers = ["admin", "master"].includes(user.role);

  const updateForm = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    try {
      await api.post("/team", {
        ...form,
        email: form.email || null,
        role: canManageUsers ? form.role : "officer",
        reports_to_id: form.reports_to_id || null,
      });
      toast.success("Worker added");
      setAdding(false);
      setForm(blankWorkerForm());
      loadTeam();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const deleteMember = async () => {
    try {
      await api.delete(`/team/${deleting.id}`);
      toast.success("User deleted and records reassigned");
      setDeleting(null);
      loadTeam();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <div className="page users-management-page" data-testid="team-page">
      <header className="page-header">
        <div>
          <h1>User</h1>
          <p>Manage worker User IDs, roles, reporting lines, and account access.</p>
        </div>
        <Button onClick={() => setAdding(true)} data-testid="add-officer-button">
          <UserPlus size={17} /> Add Worker
        </Button>
      </header>
      <div className="table-wrap user-management-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>User ID</th>
              <th>Role</th>
              <th>Reports To</th>
              <th>Created</th>
              {canManageUsers && <th>Account</th>}
              {canManageUsers && <th>Delete</th>}
            </tr>
          </thead>
          <tbody>
            {team.map((member) => (
              <tr key={member.id} data-testid={`team-member-${member.id}`}>
                <td><strong>{member.name}</strong></td>
                <td data-testid={`worker-user-id-${member.id}`}>{member.user_id || "—"}</td>
                <td><span className="status status-current">{member.role}</span></td>
                <td>{member.parent_id ? parentNames.get(member.parent_id) || "Unknown" : "—"}</td>
                <td>{member.created_at ? new Date(member.created_at).toLocaleDateString() : "—"}</td>
                {canManageUsers && (
                  <td>
                    {(
                      <button
                        className="customer-icon-button"
                        onClick={() => setEditing(member)}
                        data-testid={`edit-worker-credentials-${member.id}`}
                        aria-label={`Edit ${member.name} account`}
                      >
                        <Key size={17} />
                      </button>
                    )}
                  </td>
                )}
                {canManageUsers && (
                  <td>
                    {member.role !== "admin" && (
                      <button
                        className="customer-icon-button customer-delete-button"
                        onClick={() => setDeleting(member)}
                        data-testid={`delete-user-${member.id}`}
                        aria-label={`Delete ${member.name}`}
                      >
                        <Trash size={17} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent data-testid="add-officer-dialog">
          <DialogHeader>
            <DialogTitle>Add Worker</DialogTitle>
          </DialogHeader>
          <form className="dialog-form" onSubmit={submit}>
            <Label htmlFor="officer-name">Full Name</Label>
            <Input
              id="officer-name"
              data-testid="officer-name-input"
              value={form.name}
              onChange={(event) => updateForm("name", event.target.value)}
              required
            />
            <Label htmlFor="officer-user-id">User ID</Label>
            <Input
              id="officer-user-id"
              data-testid="officer-user-id-input"
              value={form.user_id}
              onChange={(event) => updateForm("user_id", event.target.value)}
              required
            />
            <Label htmlFor="officer-email">Contact Email (Optional)</Label>
            <Input
              id="officer-email"
              data-testid="officer-email-input"
              type="email"
              value={form.email}
              onChange={(event) => updateForm("email", event.target.value)}
            />
            <Label htmlFor="officer-password">Temporary Password</Label>
            <Input
              id="officer-password"
              data-testid="officer-password-input"
              type="password"
              minLength={8}
              value={form.password}
              onChange={(event) => updateForm("password", event.target.value)}
              required
            />
            {canManageUsers && (
              <>
                <Label htmlFor="officer-role">Role</Label>
                <select
                  id="officer-role"
                  data-testid="officer-role-select"
                  value={form.role}
                  onChange={(event) => updateForm("role", event.target.value)}
                >
                  <option value="officer">Officer</option>
                  <option value="master">Master</option>
                </select>
              </>
            )}
            {canManageUsers && (
              <>
                <Label htmlFor="officer-reports-to">Reports To</Label>
                <select
                  id="officer-reports-to"
                  data-testid="officer-reports-to-select"
                  value={form.reports_to_id}
                  onChange={(event) => updateForm("reports_to_id", event.target.value)}
                >
                  <option value="">Select an up-line user</option>
                  {reportingOptions.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.label}
                      </option>
                    ))}
                </select>
              </>
            )}
            <Button type="submit" data-testid="add-officer-submit-button">
              Create Worker
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <WorkerCredentialsDialog worker={editing} onClose={() => setEditing(null)} onDone={loadTeam} />
      <Dialog open={Boolean(deleting)} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Delete user?</DialogTitle>
          </DialogHeader>
          <p className="delete-note" data-testid="delete-user-confirmation">
            {deleting?.name}'s customers, loans, repayment records, and downline will be
            reassigned to their up-line. This cannot be undone.
          </p>
          <div className="dialog-actions">
            <Button
              variant="outline"
              onClick={() => setDeleting(null)}
              data-testid="cancel-delete-user-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteMember}
              data-testid="confirm-delete-user-button"
            >
              Delete User
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}